import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Clock, 
  Lock, 
  Search,
  Filter,
  ChevronDown,
  Star,
  Eye,
  X,
  Volume2,
  VolumeX,
  Maximize,
  Pause,
  Loader2,
  Crown,
  AlertCircle
} from 'lucide-react';
import { 
  AnimatedCard, 
  StaggerContainer, 
  StaggerItem, 
  FadeIn,
} from '../components/ui/AnimatedCard';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';

interface Video {
  id: number;
  title: string;
  titleBn: string;
  description: string;
  classId: number;
  subjectId: number;
  chapterId: number;
  durationSeconds: number;
  thumbnailUrl: string;
  isPremium: boolean;
  views: number;
  rating: number;
  videoUrl: string | null;
}

export function Videos() {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [playingVideo, setPlayingVideo] = useState<Video | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  // Fetch videos from API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await api.get('/api/videos');
        setVideos(response.data);
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  // Handle video errors
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleError = () => {
      setVideoError('Failed to load video. Please try again.');
      setIsPlaying(false);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [playingVideo]);

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const filteredVideos = videos.filter(video => {
    if (searchQuery && !video.title?.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !video.titleBn?.includes(searchQuery)) return false;
    if (selectedClass && video.classId !== selectedClass) return false;
    return true;
  });

  const handlePlayVideo = (video: Video) => {
    if (video.isPremium && !user?.isPremium) {
      setShowPremiumModal(true);
      return;
    }
    setVideoError(null);
    setPlayingVideo(video);
    setIsPlaying(true);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {
          setVideoError('Unable to play video. Please try again.');
        });
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (playerRef.current) {
      if (!document.fullscreenElement) {
        playerRef.current.requestFullscreen().catch(() => {});
      } else {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const closePlayer = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }
    setPlayingVideo(null);
    setIsPlaying(false);
    setVideoError(null);
  };

  const handleUpgradeClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/payments');
    }
    setShowPremiumModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Video Lessons</h1>
            <p className="text-slate-500 mt-1">Watch and learn from expert teachers</p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </FadeIn>

      {/* Search */}
      <FadeIn delay={0.1}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
          />
        </div>
      </FadeIn>

      {/* Filters */}
      {showFilters && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4"
        >
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Class</label>
            <div className="flex flex-wrap gap-2">
              {[9, 10, 11, 12].map(cls => (
                <button
                  key={cls}
                  onClick={() => setSelectedClass(selectedClass === cls ? null : cls)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedClass === cls
                      ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Class {cls}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Video Player */}
      <AnimatePresence>
        {playingVideo && (
          <FadeIn>
            <div ref={playerRef} className="bg-black rounded-2xl overflow-hidden relative group">
              <video
                ref={videoRef}
                src={playingVideo.videoUrl || undefined}
                className="w-full aspect-video"
                autoPlay
                playsInline
                controls
                onClick={togglePlay}
              />
              
              {/* Error overlay */}
              {videoError && (
                <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                  <div className="text-center text-white">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                    <p className="text-lg">{videoError}</p>
                    <button 
                      onClick={closePlayer}
                      className="mt-4 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {/* Close button (always visible) */}
              <button 
                onClick={closePlayer}
                className="absolute top-4 right-4 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </FadeIn>
        )}
      </AnimatePresence>

      {/* Premium Upgrade Modal */}
      <AnimatePresence>
        {showPremiumModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowPremiumModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-accent-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Premium Content</h2>
              <p className="text-slate-500 mb-6">
                This video is only available for premium members. Upgrade your account to unlock all premium videos and features.
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleUpgradeClick}
                  className="w-full py-3 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors"
                >
                  {isAuthenticated ? 'Upgrade to Premium' : 'Login to Upgrade'}
                </button>
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="w-full py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Grid */}
      <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVideos.map((video) => (
          <StaggerItem key={video.id}>
            <AnimatedCard className="overflow-hidden group cursor-pointer" hover>
              {/* Thumbnail */}
              <div 
                className="relative aspect-video bg-slate-200 overflow-hidden"
                onClick={() => handlePlayVideo(video)}
              >
                {video.thumbnailUrl ? (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white/50" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center"
                  >
                    {video.isPremium && !user?.isPremium ? (
                      <Lock className="w-8 h-8 text-accent-600" />
                    ) : (
                      <Play className="w-8 h-8 text-primary-600 ml-1" />
                    )}
                  </motion.div>
                </div>
                {video.isPremium && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-accent-500 text-white text-xs font-bold rounded-lg flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    Premium
                  </div>
                )}
                {video.durationSeconds > 0 && (
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded-lg flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(video.durationSeconds)}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-bold text-slate-900 mb-1">{video.titleBn || video.title}</h3>
                <p className="text-sm text-slate-500 mb-3 line-clamp-2">{video.description}</p>
                
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {video.views || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-500" />
                      {video.rating || 0}
                    </span>
                  </div>
                  {video.isPremium && !user?.isPremium ? (
                    <button 
                      onClick={() => handlePlayVideo(video)}
                      className="flex items-center gap-1 text-accent-600 font-medium hover:text-accent-700"
                    >
                      <Lock className="w-3 h-3" />
                      Premium
                    </button>
                  ) : (
                    <button 
                      onClick={() => handlePlayVideo(video)}
                      className="text-primary-600 font-medium hover:text-primary-700"
                    >
                      Watch Now
                    </button>
                  )}
                </div>
              </div>
            </AnimatedCard>
          </StaggerItem>
        ))}
      </StaggerContainer>

      {filteredVideos.length === 0 && !loading && (
        <div className="text-center py-12">
          <Play className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-600">No videos found</h3>
          <p className="text-slate-400">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
}
